




















































import { Vue, Component, Prop } from 'vue-property-decorator';
import Spinner from '@/atomics/loaders/Spinner.vue';
import idealName from '@/utils/ideal-name';
import capitalize from '@/utils/text';
import { getUserInfo } from '@/store/plugins/userInfoPlugin';
import { AuditEntry, AuditState } from '@/api/audit';

// Actions that read as a sentence. Anything else falls back to "agent · queue" so a new action
// added on the backend degrades to the plain summary instead of rendering a missing i18n key.
const SENTENCE_ACTIONS = ['agent_attached', 'agent_detached'];

interface TimelineEntry {
    key: string;
    action: string;
    summary: string;
    transition: string;
    time: string;
    actor: string;
}

interface TimelineGroup {
    day: string;
    entries: TimelineEntry[];
}

function badgeClassFor(action: string): string {
    if (action === 'agent_attached') {
        return 'bg-green-100 text-green-800';
    }

    if (action === 'agent_detached') {
        return 'bg-red-100 text-red-800';
    }

    return 'bg-gray-100 text-gray-800';
}

// Picallex returns "YYYY-MM-DD HH:MM:SS"; Safari needs the ISO separator.
function parseDate(createdAt: string | null): Date | null {
    if (!createdAt) {
        return null;
    }

    const date = new Date(createdAt.replace(' ', 'T'));

    return Number.isNaN(date.getTime()) ? null : date;
}

function timeOf(createdAt: string | null): string {
    const date = parseDate(createdAt);

    return date ? date.toLocaleTimeString() : '-';
}

function stateLabelOf(state: AuditState | null): string {
    if (!state) {
        return '';
    }

    if (typeof state.queue === 'string') {
        return idealName(state.queue);
    }

    if (Array.isArray(state.queues)) {
        return state.queues.map((queue) => idealName(String(queue))).join(', ');
    }

    return '';
}

function extensionOf(agentName: string | null): string {
    const match = String(agentName || '').match(/^(\d+)@/);

    return match ? match[1] : '';
}

// Rows written before the panel started sending agentLabel carry no name, and rows whose actor
// lacked permission to see other extensions never will. Falling back to the extensions map shows
// the name for those too. It is the owner's name *today*, so a recorded label always wins: the
// trail must keep showing who the agent was at the time of the change.
function labelFromSession(extension: string): string {
    if (extension === '') {
        return '';
    }

    return getUserInfo()?.users?.[extension] || '';
}

// Mirrors the agent card of the panel, which renders "1067 - Valentin", so the trail names an
// agent exactly as the operator saw it on screen when making the change.
function agentDisplayOf(entry: AuditEntry): string {
    const extension = extensionOf(entry.agentName);
    const name = entry.agentLabel || labelFromSession(extension);
    const label = name ? capitalize(name) : '';

    if (extension && label) {
        return `${extension} - ${label}`;
    }

    return extension || label || capitalize(idealName(entry.agentName || '')) || '-';
}

function queueDisplayOf(entry: AuditEntry): string {
    return entry.queueLabel || idealName(entry.queueName || '') || '-';
}

function transitionOf(entry: AuditEntry): string {
    const before = stateLabelOf(entry.beforeState);
    const after = stateLabelOf(entry.afterState);

    if (before && after) {
        return `${before} → ${after}`;
    }

    return before || after;
}

@Component({ components: { Spinner } })
export default class AuditTimeline extends Vue {
    @Prop({ default: () => [] }) readonly entries!: AuditEntry[];

    @Prop(Boolean) readonly isLoading!: boolean;

    @Prop({ default: '' }) readonly error!: string;

    @Prop(Boolean) readonly hideQueue!: boolean;

    badgeClass = badgeClassFor;

    get hasEntries(): boolean {
        return this.entries.length > 0;
    }

    get groups(): TimelineGroup[] {
        const groups: TimelineGroup[] = [];

        this.entries.forEach((entry, index) => {
            const day = this.dayOf(entry.createdAt);
            const last = groups[groups.length - 1];
            const timelineEntry = this.toTimelineEntry(entry, index);

            if (last && last.day === day) {
                last.entries.push(timelineEntry);
                return;
            }

            groups.push({ day, entries: [timelineEntry] });
        });

        return groups;
    }

    actionLabel(action: string): string {
        return String(this.$t(`audit_action_${action}`));
    }

    private toTimelineEntry(entry: AuditEntry, index: number): TimelineEntry {
        return {
            key: `${entry.id ?? 'n'}-${index}`,
            action: entry.action,
            summary: this.summaryOf(entry),
            transition: transitionOf(entry),
            time: timeOf(entry.createdAt),
            actor: this.actorOf(entry),
        };
    }

    private summaryOf(entry: AuditEntry): string {
        const agent = agentDisplayOf(entry);

        if (!SENTENCE_ACTIONS.includes(entry.action)) {
            return this.hideQueue ? agent : `${agent} · ${queueDisplayOf(entry)}`;
        }

        const user = entry.performedByUsername || '-';

        // The sentence always names the queue, drawer included: reading "de la cola 8004" beats
        // "de esta cola" even where the drawer header already says which queue it is.
        return String(this.$t(`audit_sentence_${entry.action}`, {
            user,
            agent,
            queue: queueDisplayOf(entry),
        }));
    }

    // The sentence already names the actor, so repeating it under the timestamp would be noise.
    private actorOf(entry: AuditEntry): string {
        if (SENTENCE_ACTIONS.includes(entry.action)) {
            return '';
        }

        return String(this.$t('audit_performed_by', { user: entry.performedByUsername || '-' }));
    }

    private dayOf(createdAt: string | null): string {
        const date = parseDate(createdAt);

        return date ? date.toLocaleDateString() : String(this.$t('audit_unknown_date'));
    }
}
